import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiOrchidFeedback } from "../api/orchidFeedback";
import { toast, ToastContainer } from "react-toastify";
import { useTour } from "../tours/useTour";
import "react-toastify/dist/ReactToastify.css";
import { Doughnut, Bar } from "react-chartjs-2";

// ---- Friendly labels ----
const AREA_LABELS = { photo: "Photo", data: "Data" };
// Areas are structural (baked into SegArea + the dashboard chart), so their label
// and badge maps stay here. Categories and the status set are owned by the
// backend (GET /orchid-feedback/options) — categories per-company config,
// statuses a fixed 5.
const AREA_BADGE = { photo: "tw-bg-sky-100 tw-text-sky-700", data: "tw-bg-violet-100 tw-text-violet-700" };
const STATUS_BADGE = {
  "": "tw-bg-gray-100 tw-text-gray-600",
  foursite_issue: "tw-bg-amber-100 tw-text-amber-700",
  return_to_orchid: "tw-bg-indigo-100 tw-text-indigo-700",
  return_to_orchid_clarification: "tw-bg-amber-100 tw-text-amber-700",
  qa_review: "tw-bg-rose-100 tw-text-rose-700",
  discussion_required: "tw-bg-violet-100 tw-text-violet-700",
};

const chartPalette = ["#005088", "#00acdc", "#94ba33", "#f57c00", "#d71433", "#7c3aed", "#0d9488", "#b45309"];

const inputCls =
  "tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm tw-bg-white focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-sky-300 focus:tw-border-sky-400";
const selectCls = inputCls;
const labelCls = "tw-block tw-text-xs tw-font-semibold tw-text-gray-500 tw-mb-1";

function mutationOk(res) {
  if (res && res.data && typeof res.data.status !== "undefined") {return res.data.status === 200;}
  return !!res && res.status >= 200 && res.status < 300;
}

function emptyIssueRow() {
  return {
    concernArea: "photo",
    concernCategory: "missing_required_photos",
    missingPhotoSectionId: "",
    orchidNotes: "",
    foursiteNotes: "",
    foursiteResponse: "",
    isInspectorIssue: false,
    status: "",
    foursiteActions: [],
  };
}

// ---- small primitives ----
function Badge({ text, className }) {
  return (
    <span className={`tw-inline-block tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-whitespace-nowrap ${className}`}>
      {text}
    </span>
  );
}

function Spinner({ size = 20 }) {
  return (
    <span
      className="tw-inline-block tw-animate-spin tw-rounded-full tw-border-2 tw-border-sky-300 tw-border-t-transparent"
      style={{ width: size, height: size }}
    />
  );
}

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="tw-text-rose-500"> *</span>}</label>
      {children}
      {hint && <p className="tw-text-xs tw-text-amber-600 tw-mt-1">{hint}</p>}
    </div>
  );
}

function TextInput(props) {
  return <input {...props} className={`${inputCls} ${props.className || ""}`} />;
}

function TextArea(props) {
  return <textarea {...props} className={`${inputCls} ${props.className || ""}`} />;
}

function Select({ value, onChange, children, className, ...rest }) {
  return (
    <select value={value} onChange={onChange} className={`${selectCls} ${className || ""}`} {...rest}>
      {children}
    </select>
  );
}

function Check({ checked, onChange, label }) {
  return (
    <label className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-text-sm tw-text-gray-700">
      <input type="checkbox" checked={checked} onChange={onChange}
        className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-sky-600 focus:tw-ring-sky-300" />
      {label}
    </label>
  );
}

// Segmented two-option picker for the Concern Area.
function SegArea({ value, onChange }) {
  const opt = (val, label) => 
    <button type="button" onClick={() => onChange(val)}
      className={`tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-lg tw-border tw-transition-colors
        ${value === val ? "tw-bg-sky-600 tw-text-white tw-border-sky-600" : "tw-bg-white tw-text-gray-600 tw-border-gray-300 hover:tw-bg-gray-50"}`}>
      {label}
    </button>
  ;
  return <div className="tw-inline-flex tw-gap-2">{opt("photo", "Photo")}{opt("data", "Data")}</div>;
}

function Btn({ variant, children, ...rest }) {
  const base = "tw-inline-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed";
  const styles = variant === "primary"
    ? "tw-bg-sky-600 tw-text-white hover:tw-bg-sky-700"
    : variant === "danger"
      ? "tw-text-rose-600 hover:tw-bg-rose-50"
      : "tw-border tw-border-gray-300 tw-text-gray-700 tw-bg-white hover:tw-bg-gray-50";
  return <button {...rest} className={`${base} ${styles}`}>{children}</button>;
}

function Modal({ open, onClose, title, children, footer, wide }) {
  if (!open) {return null;}
  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-start tw-justify-center tw-overflow-y-auto tw-py-8 tw-px-4"
      style={{ background: "rgba(15,23,42,0.45)" }} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className={`tw-w-full ${wide ? "tw-max-w-3xl" : "tw-max-w-md"} tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-my-auto`}
        onClick={(e) => e.stopPropagation()}>
        <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-100">
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-800">{title}</h3>
          <button onClick={onClose} className="tw-text-gray-400 hover:tw-text-gray-700 tw-text-2xl tw-leading-none">&times;</button>
        </div>
        <div className="tw-px-6 tw-py-5 tw-max-h-[70vh] tw-overflow-y-auto">{children}</div>
        {footer && <div className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-py-3 tw-border-t tw-border-gray-100 tw-bg-gray-50 tw-rounded-b-2xl">{footer}</div>}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-5 tw-h-full tw-min-h-[7.5rem] tw-flex tw-flex-col tw-gap-1">
      <span className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">{label}</span>
      <span className={`tw-text-2xl tw-font-bold tw-leading-tight ${accent || "tw-text-gray-800"}`}>{value}</span>
      {sub && <span className="tw-text-sm tw-text-gray-500">{sub}</span>}
    </div>
  );
}

function issueWord(n) { return `${n} issue${n === 1 ? "" : "s"}`; }

function Empty() { return <div className="tw-text-center tw-text-gray-300 tw-py-8 tw-text-sm">No data</div>; }

export default function OrchidFeedback() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sectionFilter, setSectionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const [logOpen, setLogOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [editIssue, setEditIssue] = useState(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [issues, setIssues] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [opts, setOpts] = useState(null);

  // Options + photo sections load once on mount.
  useEffect(() => {
    let alive = true;
    apiOrchidFeedback.options()
      .then((res) => { if (alive) {setOpts(res.data.result);} })
      .catch(() => {});
    apiOrchidFeedback.listPhotoSections()
      .then((res) => { if (alive) {setSections(res.data.result || []);} })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Stats refetch when the date range changes.
  const loadStats = useCallback(() => {
    setStatsLoading(true);
    apiOrchidFeedback.stats({ from, to })
      .then((res) => setStats(res.data.result))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, [from, to]);
  useEffect(() => { loadStats(); }, [loadStats]);

  // Issues list refetch when any filter or the page changes.
  const loadIssues = useCallback(() => {
    setListLoading(true);
    const params = { limit: pageSize, offset: page * pageSize };
    if (from) {params.from = from;}
    if (to) {params.to = to;}
    if (areaFilter) {params.area = areaFilter;}
    if (categoryFilter) {params.category = categoryFilter;}
    if (sectionFilter) {params.sectionId = sectionFilter;}
    if (statusFilter) {params.status = statusFilter;}
    if (search) {params.search = search;}
    apiOrchidFeedback.listIssues(params)
      .then((res) => { setIssues(res.data.result?.items || []); setTotal(res.data.result?.total || 0); })
      .catch(() => { setIssues([]); setTotal(0); })
      .finally(() => setListLoading(false));
  }, [from, to, areaFilter, categoryFilter, sectionFilter, statusFilter, search, page]);
  useEffect(() => { loadIssues(); }, [loadIssues]);

  const refresh = () => { loadStats(); loadIssues(); };

  const categoryLabels = useMemo(() => {
    const m = {};
    (opts?.categories || []).forEach((c) => { m[c.key] = c.label; });
    return m;
  }, [opts]);
  const categoriesByArea = useMemo(() => {
    const m = {};
    (opts?.categories || []).forEach((c) => { (m[c.area] = m[c.area] || []).push({ value: c.key, label: c.label }); });
    return m;
  }, [opts]);
  const statusLabels = useMemo(() => {
    const m = {};
    (opts?.statuses || []).forEach((s) => { m[s.key] = s.label; });
    return m;
  }, [opts]);
  const foursiteActionOptions = useMemo(() => opts?.foursiteActions || [], [opts]);
  const foursiteActionLabels = useMemo(() => {
    const m = {};
    (opts?.foursiteActions || []).forEach((a) => { m[a.key] = a.label; });
    return m;
  }, [opts]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this issue? This cannot be undone.")) {return;}
    const res = await apiOrchidFeedback.deleteIssue(id);
    if (mutationOk(res)) { toast.success("Issue deleted"); refresh(); }
    else { toast.error(res.data && res.data.message || "Failed to delete"); }
  };

  const tourSteps = useMemo(() => [
    {
      element: '[data-tour=ofb-header]',
      popover: {
        title: "Orchid Feedback",
        description: "Log and report on quality issues Orchid (our inspection vendor) sends back about completed inspections.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour=ofb-stats]',
      popover: {
        title: "At a glance",
        description: "Dashboard totals and the most common issue, photo/data split, top inspector, and most-missing photo sections. Use the date range above to scope it.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour=ofb-log]',
      popover: {
        title: "Log new issue",
        description: "Click here, enter a policy number to look up the inspection, then record one or more quality issues from an Orchid feedback report.",
        side: "bottom", align: "end",
      },
    },
    {
      element: '[data-tour=ofb-sections]',
      popover: {
        title: "Manage Photo Sections",
        description: "Configure the dropdown used when logging missing or poor-quality photos (e.g. Water Heater label) so reporting can spot problem photo requirements over time.",
        side: "bottom", align: "end",
      },
    },
    {
      element: '[data-tour=ofb-issues]',
      popover: {
        title: "Issues list",
        description: "Browse every logged issue. Filter by area, category, section, or status, or search by policy / inspector / notes. Use Edit to set a status and add a Four Site response, or Delete to remove an entry.",
        side: "top", align: "start",
      },
    },
  ], []);

  const { startTour, showButton } = useTour({ tourKey: "orchid-feedback", steps: tourSteps });

  return (
    <div className="tw-max-w-screen-xl tw-mx-auto tw-px-4 tw-py-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div data-tour="ofb-header" className="tw-flex tw-flex-wrap tw-items-center tw-gap-4 tw-mb-6">
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800">Orchid Feedback</h1>
          <p className="tw-text-sm tw-text-gray-500">Quality issues reported by our inspection vendor, Orchid.</p>
        </div>
        <div className="tw-ml-auto tw-flex tw-flex-wrap tw-gap-2">
          <Btn onClick={() => setReportsOpen(true)} title="Download a report (overview, training, vendor, internal)">Reports</Btn>
          {showButton && <button onClick={startTour} className="tw-px-3 tw-py-1.5 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50">Take the tour</button>}
          <Btn data-tour="ofb-sections" onClick={() => setSectionsOpen(true)}>Manage Photo Sections</Btn>
          <Btn onClick={() => setPasteOpen(true)} title="Paste a weekly Orchid feedback email and parse it into draft issues">Paste feedback</Btn>
          <Btn variant="primary" data-tour="ofb-log" onClick={() => setLogOpen(true)}>＋ Log new issue</Btn>
        </div>
      </div>

      <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-mb-6">
        <Field label="From"><TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
        {(from || to) && <Btn onClick={() => { setFrom(""); setTo(""); }}>Clear dates</Btn>}
      </div>

      <div data-tour="ofb-stats">
        <Dashboard stats={stats} loading={statsLoading} categoryLabels={categoryLabels} foursiteActionLabels={foursiteActionLabels} />
      </div>

      <div data-tour="ofb-issues" className="tw-mt-8 tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 tw-px-5 tw-py-4 tw-border-b tw-border-gray-100">
          <h3 className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mr-auto">Issues</h3>
          <Field label="Area">
            <Select value={areaFilter} onChange={(e) => { setAreaFilter(e.target.value); setPage(0); }}>
              <option value="">All</option><option value="photo">Photo</option><option value="data">Data</option>
            </Select>
          </Field>
          <Field label="Category">
            <Select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }}>
              <option value="">All</option>
              {Object.entries(categoryLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Section">
            <Select value={sectionFilter} onChange={(e) => { setSectionFilter(e.target.value); setPage(0); }}>
              <option value="">All</option>
              {sections.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Status">
            <Select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
              <option value="">All</option>
              {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
          </Field>
          <Field label="Search">
            <TextInput type="text" placeholder="Policy / inspector / notes…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} style={{ minWidth: 220 }} />
          </Field>
        </div>

        {listLoading
          ? <div className="tw-p-10 tw-flex tw-justify-center"><Spinner size={28} /></div>
          : issues.length === 0
            ? <div className="tw-p-10 tw-text-center tw-text-gray-400">No issues logged {search || areaFilter || categoryFilter || statusFilter || from || to ? "for these filters" : "yet — click “Log new issue” to start"}.</div>
            : <IssuesTable issues={issues} sections={sections} categoryLabels={categoryLabels} statusLabels={statusLabels} foursiteActionLabels={foursiteActionLabels} onEdit={(i) => setEditIssue(i)} onDelete={handleDelete} />
        }

        {total > pageSize && 
          <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-t tw-border-gray-100 tw-text-sm tw-text-gray-500">
            <span>Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} of {total}</span>
            <div className="tw-flex tw-gap-2">
              <Btn onClick={() => setPage((p) => p - 1)} disabled={page === 0}>Previous</Btn>
              <Btn onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * pageSize >= total}>Next</Btn>
            </div>
          </div>
        }
      </div>

      <LogIssueDialog open={logOpen} onClose={() => setLogOpen(false)} sections={sections} categoriesByArea={categoriesByArea} statusLabels={statusLabels} foursiteActionOptions={foursiteActionOptions}
        onSaved={() => { setLogOpen(false); refresh(); toast.success("Issues logged"); }} />
      <PasteFeedbackDialog open={pasteOpen} onClose={() => setPasteOpen(false)} sections={sections} categoriesByArea={categoriesByArea}
        onSaved={() => { setPasteOpen(false); refresh(); toast.success("Feedback imported"); }} />
      <EditIssueDialog open={!!editIssue} issue={editIssue} onClose={() => setEditIssue(null)} sections={sections} categoriesByArea={categoriesByArea} statusLabels={statusLabels} foursiteActionOptions={foursiteActionOptions}
        onSaved={() => { setEditIssue(null); refresh(); toast.success("Issue updated"); }} />
      <PhotoSectionsDialog open={sectionsOpen} onClose={() => setSectionsOpen(false)} />
      <ReportsDialog open={reportsOpen} onClose={() => setReportsOpen(false)} />
    </div>
  );
}

// ---- Dashboard ----
function Dashboard({ stats, loading, categoryLabels, foursiteActionLabels }) {
  if (loading) {
    return <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-10 tw-flex tw-justify-center"><Spinner size={28} /></div>;
  }
  if (!stats) {return null;}

  const total = stats.total || 0;
  const photoCount = (stats.byArea.find((a) => a.label === "photo") || { count: 0 }).count;
  const dataCount = (stats.byArea.find((a) => a.label === "data") || { count: 0 }).count;
  const topCategory = stats.byCategory[0];
  const topInspector = stats.byInspector[0];

  const areaData = {
    labels: ["Photo", "Data"],
    datasets: [{ data: [photoCount, dataCount], backgroundColor: ["#00acdc", "#7c3aed"], borderWidth: 0 }],
  };
  const catData = {
    labels: stats.byCategory.map((c) => categoryLabels[c.label] || c.label),
    datasets: [{ data: stats.byCategory.map((c) => c.count), backgroundColor: chartPalette, borderWidth: 0 }],
  };
  const actionData = {
    labels: stats.byFoursiteAction.map((a) => foursiteActionLabels[a.label] || a.label),
    datasets: [{ data: stats.byFoursiteAction.map((a) => a.count), backgroundColor: chartPalette, borderWidth: 0 }],
  };

  return (
    <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-4 tw-gap-4">
      <StatCard label="Total Issues" value={total} sub={stats.notOurInspection > 0 ? `${stats.notOurInspection} not our inspections` : "all Four Site inspections"} accent="tw-text-gray-800" />
      <StatCard label="Photo / Data" value={`${photoCount} / ${dataCount}`} sub={total ? `${Math.round(photoCount / total * 100)}% photos` : "—"} accent="tw-text-sky-600" />
      <StatCard label="Top Category" value={topCategory ? categoryLabels[topCategory.label] || topCategory.label : "—"} sub={topCategory ? issueWord(topCategory.count) : ""} accent="tw-text-amber-600" />
      <StatCard label="Top Inspector" value={topInspector ? topInspector.label || "Unknown" : "—"} sub={topInspector ? issueWord(topInspector.count) : ""} accent="tw-text-indigo-600" />

      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-5 lg:tw-col-span-2">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">By Area</h3>
        {total > 0
          ? <Doughnut data={areaData} options={{ maintainAspectRatio: true, legend: { position: "bottom", labels: { padding: 12, fontColor: "#4b5563" } } }} />
          : <Empty />}
      </div>
      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-5 lg:tw-col-span-2">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Issues by Category</h3>
        {stats.byCategory.length > 0
          ? <Bar data={catData} options={{ maintainAspectRatio: true, legend: { display: false }, scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }], xAxes: [{ ticks: { autoSkip: false, fontColor: "#4b5563" } }] } }} />
          : <Empty />}
      </div>
      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-5 lg:tw-col-span-2">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">By Four Site Action</h3>
        {stats.byFoursiteAction.length > 0
          ? <Bar data={actionData} options={{ maintainAspectRatio: true, legend: { display: false }, scales: { yAxes: [{ ticks: { beginAtZero: true, precision: 0 } }], xAxes: [{ ticks: { autoSkip: false, fontColor: "#4b5563" } }] } }} />
          : <Empty />}
      </div>
      <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-gray-100 tw-p-5 lg:tw-col-span-2">
        <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3">Most-missing Photo Sections</h3>
        {stats.bySection.length > 0
          ? <ul className="tw-space-y-2">
            {stats.bySection.slice(0, 6).map((s, i) => 
              <li key={i} className="tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-sm tw-text-gray-700 tw-truncate tw-mr-2">{s.label || "—"}</span>
                <Badge text={s.count} className="tw-bg-rose-100 tw-text-rose-700" />
              </li>
            )}
          </ul>
          : <Empty />}
      </div>
    </div>
  );
}

// ---- Issues table ----
function IssuesTable({ issues, sections, categoryLabels, statusLabels, foursiteActionLabels, onEdit, onDelete }) {
  const sectionName = (id) => (sections.find((s) => s.id === id) || {}).name;
  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-100 tw-text-sm">
        <thead>
          <tr className="tw-bg-sky-50 tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
            <Th>Policy</Th><Th>Inspector</Th><Th>Area</Th><Th>Category</Th><Th>Missing Section</Th>
            <Th>Report</Th><Th>Status</Th><Th>Four Site Action</Th><Th>Inspector?</Th><Th></Th>
          </tr>
        </thead>
        <tbody className="tw-divide-y tw-divide-gray-50">
          {issues.map((i) => 
            <tr key={i.id} className="hover:tw-bg-gray-50">
              <Td>{i.notOurInspection
                ? <span className="tw-font-medium tw-text-gray-800">{i.policyNo}</span>
                : <a href={i.reportedInReportDate ? `/inspections/${encodeURIComponent(i.policyNo)}?reportDate=${i.reportedInReportDate}` : `/inspections/${encodeURIComponent(i.policyNo)}`} target="_blank" rel="noopener noreferrer" className="tw-font-medium tw-text-sky-700 hover:tw-underline">{i.policyNo}</a>}{i.notOurInspection && <div className="tw-mt-1"><Badge text="Not FS" className="tw-bg-rose-100 tw-text-rose-700" /></div>}</Td>
              <Td>{i.inspectorName || "—"}</Td>
              <Td><Badge text={AREA_LABELS[i.concernArea] || i.concernArea} className={AREA_BADGE[i.concernArea] || "tw-bg-gray-100"} /></Td>
              <Td><span className="tw-text-gray-700">{categoryLabels[i.concernCategory] || i.concernCategory}</span></Td>
              <Td>{i.missingPhotoSectionName || sectionName(i.missingPhotoSectionId) || "—"}</Td>
              <Td>{i.reportedInReportDate || "—"}</Td>
              <Td>{i.status ? <Badge text={statusLabels[i.status] || i.status} className={STATUS_BADGE[i.status] || "tw-bg-gray-100"} /> : "—"}</Td>
              <Td>{(i.foursiteActions || []).length ? i.foursiteActions.map((k) => foursiteActionLabels[k] || k).join(", ") : "—"}</Td>
              <Td>{i.isInspectorIssue ? <Badge text="Yes" className="tw-bg-rose-100 tw-text-rose-700" /> : "—"}</Td>
              <Td>
                <div className="tw-flex tw-gap-1 tw-whitespace-nowrap">
                  <button className="tw-text-sky-600 hover:tw-underline" onClick={() => onEdit(i)}>Edit</button>
                  <span className="tw-text-gray-300">|</span>
                  <button className="tw-text-rose-600 hover:tw-underline" onClick={() => onDelete(i.id)}>Delete</button>
                </div>
              </Td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
function Th({ children }) { return <th className="tw-px-4 tw-py-3 tw-text-left tw-font-semibold">{children}</th>; }
function Td({ children }) { return <td className="tw-px-4 tw-py-2 tw-text-gray-700">{children}</td>; }

// ---- Log new issue dialog (multiple rows) ----
function LogIssueDialog({ open, onClose, sections, categoriesByArea, statusLabels, foursiteActionOptions, onSaved }) {
  const [policyNo, setPolicyNo] = useState("");
  const [lookup, setLookup] = useState(null);
  const [looking, setLooking] = useState(false);
  const [rows, setRows] = useState([emptyIssueRow()]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setPolicyNo(""); setLookup(null); setRows([emptyIssueRow()]); setReportDate(new Date().toISOString().slice(0, 10)); }
  }, [open]);

  const doLookup = async () => {
    if (!policyNo.trim()) {return;}
    setLooking(true);
    const res = await apiOrchidFeedback.lookup(policyNo.trim());
    setLooking(false);
    setLookup(res.data.result);
  };

  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addRow = () => setRows((rs) => [...rs, emptyIssueRow()]);
  const removeRow = (idx) => setRows((rs) => rs.filter((_, i) => i !== idx));

  const onArea = (idx, area) => {
    const cats = categoriesByArea[area] || [];
    updateRow(idx, { concernArea: area, concernCategory: cats[0] ? cats[0].value : "", missingPhotoSectionId: "" });
  };

  const save = async () => {
    if (!lookup) { toast.error("Enter a policy number and look it up first."); return; }
    // If the policy isn't a Four Site inspection, make the user explicitly
    // confirm before logging it as not-our-inspection.
    if (!lookup.found) {
      if (!window.confirm("This policy number isn't a Four Site inspection. Log the feedback anyway?")) {return;}
    }
    if (!reportDate) { toast.error("A report date is required."); return; }
    setSaving(true);
    const res = await apiOrchidFeedback.createIssues(policyNo.trim(), rows.map((r) => ({
      ...r,
      reportedInReportDate: reportDate,
      missingPhotoSectionId: r.missingPhotoSectionId === "" ? 0 : Number(r.missingPhotoSectionId),
    })));
    setSaving(false);
    if (res.data.status === 200) { onSaved(); }
    else { toast.error(res.data.message || res.data.errors && res.data.errors[0]?.message || "Failed to save"); }
  };

  const activeSections = sections.filter((s) => s.active);

  return (
    <Modal open={open} onClose={onClose} title="Log Orchid Feedback" wide
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={save} disabled={saving || !lookup}>
          {saving ? <Spinner /> : `Log ${rows.length} issue${rows.length > 1 ? "s" : ""}`}
        </Btn>
      </>}>
      <div className="tw-mb-4">
        <label className={labelCls}>Policy Number</label>
        <div className="tw-relative">
          <TextInput autoFocus value={policyNo} onChange={(e) => { setPolicyNo(e.target.value); setLookup(null); }}
            className="tw-pr-10"
            onKeyDown={(e) => e.key === "Enter" && doLookup()}
            onBlur={() => { if (policyNo.trim()) {doLookup();} }}
            placeholder="e.g. FLHO-123456-01" />
          {looking && <span className="tw-absolute tw-right-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-sky-500"><Spinner size={16} /></span>}
        </div>
      </div>

      {lookup && 
        <div className={`tw-rounded-xl tw-p-4 tw-mb-5 tw-border ${lookup.found ? "tw-bg-emerald-50 tw-border-emerald-200" : "tw-bg-rose-50 tw-border-rose-200"}`}>
          {lookup.found ?
            <>
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <span className="tw-text-emerald-600 tw-font-semibold">✓ Inspection found</span>
              </div>
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-3">
                <KV label="Insured" value={lookup.insured} />
                <KV label="Status" value={lookup.prettyStatus || lookup.status} />
                <div className="md:tw-col-span-2"><KV label="Address" value={`${lookup.address}, ${lookup.city}, ${lookup.state} ${lookup.zip}`} /></div>
                <KV label="Inspector" value={lookup.inspectorName || "—"} />
                <KV label="Created" value={lookup.createdAt || "—"} />
                <KV label="Completed" value={lookup.completedAt || "—"} />
                <KV label="Submitted" value={lookup.submittedAt || "—"} />
              </div>
            </>
            : <div className="tw-text-sm">
              <div className="tw-text-rose-700 tw-font-semibold tw-mb-1">No inspection found for this policy number in your company.</div>
              <div className="tw-text-rose-600/80">If this is an Orchid feedback report for an inspection we did not do, you can still log it — click “Log” and confirm. It will be recorded and reported separately as <b>not a Four Site inspection</b>.</div>
            </div>}
        </div>
      }

      <div className="tw-mb-4">
        <label className={labelCls}>Reported in report dated <span className="tw-text-rose-500">*</span></label>
        <TextInput type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
      </div>

      <div className="tw-space-y-4">
        {rows.map((row, idx) => 
          <IssueRow key={idx} index={idx} row={row} notFound={lookup && !lookup.found} sections={activeSections} categoriesByArea={categoriesByArea} statusLabels={statusLabels} foursiteActionOptions={foursiteActionOptions}
            onChange={(p) => updateRow(idx, p)}
            onArea={(a) => onArea(idx, a)}
            onRemove={rows.length > 1 ? () => removeRow(idx) : null}
          />
        )}
      </div>
      <Btn onClick={addRow} className="tw-mt-3">＋ Add another issue</Btn>
    </Modal>
  );
}

function IssueRow({ index, row, notFound, sections, categoriesByArea, statusLabels, foursiteActionOptions, onChange, onArea, onRemove }) {
  const cats = categoriesByArea[row.concernArea] || [];
  const showSection = row.concernArea === "photo";
  const sectionLabel = row.concernCategory === "missing_required_photos" ? "Which photo is missing?" : "Which photo is poor quality?";
  return (
    <div className="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-p-5 tw-shadow-sm">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
        <span className="tw-inline-block tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-sky-100 tw-text-sky-700 tw-text-xs tw-font-semibold">Issue {index + 1}</span>
        {onRemove && <button className="tw-text-rose-500 tw-text-sm hover:tw-underline" onClick={onRemove}>Remove</button>}
      </div>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
        <div>
          <label className={labelCls}>Concern Area</label>
          <SegArea value={row.concernArea} onChange={(a) => onArea(a)} />
        </div>
        <Field label="Concern Category">
          <Select value={row.concernCategory} onChange={(e) => onChange({ concernCategory: e.target.value, missingPhotoSectionId: "" })}>
            {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
        </Field>

        {showSection && 
          <div className="md:tw-col-span-2">
            <Field label={sectionLabel} hint={sections.length === 0 ? "No photo sections configured — add some via “Manage Photo Sections”." : undefined}>
              <Select value={row.missingPhotoSectionId} onChange={(e) => onChange({ missingPhotoSectionId: e.target.value })}>
                <option value="">— None selected —</option>
                {sections.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
              </Select>
            </Field>
          </div>
        }

        <div className="md:tw-col-span-2 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
          <Field label="Orchid Notes"><TextArea rows={2} value={row.orchidNotes} onChange={(e) => onChange({ orchidNotes: e.target.value })} /></Field>
          <Field label="Four Site Notes"><TextArea rows={2} value={row.foursiteNotes} onChange={(e) => onChange({ foursiteNotes: e.target.value })} /></Field>
        </div>

        <div className="md:tw-col-span-2">
          {notFound
            ? <Field label="Four Site Response (to Orchid)"><div className="tw-text-sm tw-text-gray-500 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-px-3 tw-py-2">This inspection was not performed by Four Site — the response is filled in automatically on the vendor report.</div></Field>
            : <Field label="Four Site Response (to Orchid)" hint="What we send back to Orchid for this issue — shown on the vendor report."><TextArea rows={2} value={row.foursiteResponse} onChange={(e) => onChange({ foursiteResponse: e.target.value })} /></Field>}
        </div>

        <div className="md:tw-col-span-2 tw-flex tw-flex-wrap tw-gap-6">
          <Check checked={row.isInspectorIssue} onChange={(e) => onChange({ isInspectorIssue: e.target.checked })} label="Inspector issue?" />
        </div>

        <div className="md:tw-col-span-2">
          <label className={labelCls}>Four Site Action</label>
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {foursiteActionOptions.map((a) =>
              <Check key={a.key} checked={row.foursiteActions.includes(a.key)}
                onChange={(e) => onChange({ foursiteActions: e.target.checked ? [...row.foursiteActions, a.key] : row.foursiteActions.filter((k) => k !== a.key) })}
                label={a.label} />
            )}
          </div>
        </div>

        <Field label="Status">
          <Select value={row.status} onChange={(e) => onChange({ status: e.target.value })}>
            <option value="">— Not set —</option>
            {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-gray-400">{label}</div>
      <div className="tw-text-sm tw-text-gray-800">{value}</div>
    </div>
  );
}

// ---- Edit single issue dialog ----
function EditIssueDialog({ open, issue, onClose, sections, categoriesByArea, statusLabels, foursiteActionOptions, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (issue) {
      setForm({
        reportedInReportDate: issue.reportedInReportDate,
        foursiteActions: issue.foursiteActions || [],
        concernArea: issue.concernArea,
        concernCategory: issue.concernCategory,
        missingPhotoSectionId: issue.missingPhotoSectionId ? String(issue.missingPhotoSectionId) : "",
        orchidNotes: issue.orchidNotes,
        foursiteNotes: issue.foursiteNotes,
        foursiteResponse: issue.foursiteResponse,
        qaFeedback: issue.qaFeedback,
        status: issue.status,
        isInspectorIssue: issue.isInspectorIssue,
      });
    } else {
      setForm(null);
    }
  }, [issue]);

  if (!issue || !form) {return null;}

  const cats = categoriesByArea[form.concernArea] || [];
  const showSection = form.concernArea === "photo";
  const sectionLabel = form.concernCategory === "missing_required_photos" ? "Which photo is missing?" : "Which photo is poor quality?";
  const onArea = (area) => setForm((f) => ({ ...f, concernArea: area, concernCategory: (categoriesByArea[area] || [])[0] ? categoriesByArea[area][0].value : "", missingPhotoSectionId: "" }));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    const res = await apiOrchidFeedback.updateIssue(issue.id, {
      ...form,
      missingPhotoSectionId: form.missingPhotoSectionId === "" ? 0 : Number(form.missingPhotoSectionId),
    });
    setSaving(false);
    if (res.data.status === 200) {onSaved();}
    else {toast.error(res.data.message || "Failed to update");}
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit Issue — ${issue.policyNo}`} wide
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        <Btn variant="primary" onClick={save} disabled={saving}>{saving ? <Spinner /> : "Save changes"}</Btn>
      </>}>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
        <Field label="Reported in report dated" required><TextInput type="date" value={form.reportedInReportDate} onChange={(e) => set({ reportedInReportDate: e.target.value })} /></Field>

        <div>
          <label className={labelCls}>Concern Area</label>
          <SegArea value={form.concernArea} onChange={onArea} />
        </div>
        <Field label="Concern Category">
          <Select value={form.concernCategory} onChange={(e) => set({ concernCategory: e.target.value, missingPhotoSectionId: "" })}>
            {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </Select>
        </Field>

        {showSection && 
          <div className="md:tw-col-span-2">
            <Field label={sectionLabel}>
              <Select value={form.missingPhotoSectionId} onChange={(e) => set({ missingPhotoSectionId: e.target.value })}>
                <option value="">— None selected —</option>
                {sections.map((s) => <option key={s.id} value={String(s.id)}>{s.name}{!s.active ? " (inactive)" : ""}</option>)}
              </Select>
            </Field>
          </div>
        }

        <Field label="Orchid Notes"><TextArea rows={2} value={form.orchidNotes} onChange={(e) => set({ orchidNotes: e.target.value })} /></Field>
        <Field label="Four Site Notes"><TextArea rows={2} value={form.foursiteNotes} onChange={(e) => set({ foursiteNotes: e.target.value })} /></Field>
        {issue.notOurInspection
          ? <div className="md:tw-col-span-2"><Field label="Four Site Response (to Orchid)"><div className="tw-text-sm tw-text-gray-500 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-px-3 tw-py-2">This inspection was not performed by Four Site — the response is filled in automatically on the vendor report.</div></Field></div>
          : <div className="md:tw-col-span-2"><Field label="Four Site Response (to Orchid)" hint="What we send back to Orchid — shown on the vendor report."><TextArea rows={3} value={form.foursiteResponse} onChange={(e) => set({ foursiteResponse: e.target.value })} /></Field></div>}
        <div className="md:tw-col-span-2"><Field label="Feedback from QA Team"><TextArea rows={2} value={form.qaFeedback} onChange={(e) => set({ qaFeedback: e.target.value })} /></Field></div>

        <div className="md:tw-col-span-2 tw-flex tw-flex-wrap tw-gap-6">
          <Check checked={form.isInspectorIssue} onChange={(e) => set({ isInspectorIssue: e.target.checked })} label="Inspector issue?" />
        </div>

        <div className="md:tw-col-span-2">
          <label className={labelCls}>Four Site Action</label>
          <div className="tw-flex tw-flex-wrap tw-gap-4">
            {foursiteActionOptions.map((a) =>
              <Check key={a.key} checked={form.foursiteActions.includes(a.key)}
                onChange={(e) => set({ foursiteActions: e.target.checked ? [...form.foursiteActions, a.key] : form.foursiteActions.filter((k) => k !== a.key) })}
                label={a.label} />
            )}
          </div>
        </div>

        <Field label="Status">
          <Select value={form.status} onChange={(e) => set({ status: e.target.value })}>
            <option value="">— Not set —</option>
            {Object.entries(statusLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
        </Field>
      </div>
    </Modal>
  );
}

// ---- Reports dialog ----
// Single launcher for all orchid-feedback reports. Vendor and internal remain
// single-date reports; overview and training also support rolling/custom ranges.
function ReportsDialog({ open, onClose }) {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState("");
  const [period, setPeriod] = useState("30");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (!open) {return;}
    setLoading(true);
    apiOrchidFeedback.reportDates()
      .then((res) => {
        const ds = res.data.result || [];
        setDates(ds);
        if (ds.length && !ds.includes(date)) {setDate(ds[0]);}
      })
      .catch(() => setDates([]))
      .finally(() => setLoading(false));
  }, [open]);

  const fmt = (d) => {
    const dt = new Date(d + "T00:00:00");
    return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const go = (url) => { window.location.href = url; onClose(); };
  const downloadDated = (kind) => {
    if (!date) { toast.error("Select a report date first."); return; }
    go(`/orchid-feedback/report/${kind}.pdf?date=${date}`);
  };
  const downloadPeriod = (kind) => {
    if (period === "single") { downloadDated(kind); return; }
    if (period === "custom") {
      if (!from || !to) { toast.error("Select both custom dates first."); return; }
      if (from > to) { toast.error("The start date must be on or before the end date."); return; }
      go(`/orchid-feedback/report/${kind}.pdf?from=${from}&to=${to}`);
      return;
    }
    go(`/orchid-feedback/report/${kind}.pdf?days=${period}`);
  };

  const reports = [
    { key: "overview", title: "Overview PDF", desc: "Management summary for the selected period.", period: true },
    { key: "training", title: "Training PDF", desc: "Inspector re-training guide for the selected period.", period: true },
    { key: "vendor", title: "Vendor response PDF", desc: "The report sent back to Orchid for a chosen date.", dated: true },
    { key: "internal", title: "Internal issue report", desc: "Full internal report (all statuses) for a chosen date. Not sent to Orchid.", dated: true },
  ];

  return (
    <Modal open={open} onClose={onClose} title="Download a report" wide>
      {loading
        ? <div className="tw-flex tw-justify-center tw-py-6"><Spinner size={24} /></div>
        : <>
          <Field label="Overview / training period">
            <Select value={period} onChange={(e) => setPeriod(e.target.value)} autoFocus>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="single">Single report date</option>
              <option value="custom">Custom date range</option>
            </Select>
          </Field>
          {period === "custom" &&
            <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mt-3">
              <Field label="From"><TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
              <Field label="To"><TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
            </div>}
          <div className="tw-mt-3">
            <Field label="Report date" hint="Used by vendor/internal reports and the single-date option above. Only dates with issues are listed.">
              <Select value={date} onChange={(e) => setDate(e.target.value)}>
                {dates.length === 0
                  ? <option value="">No issues logged yet</option>
                  : dates.map((d) => <option key={d} value={d}>{fmt(d)}</option>)}
              </Select>
            </Field>
          </div>
          <ul className="tw-space-y-2 tw-mt-4">
            {reports.map((r) =>
              <li key={r.key} className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-border tw-border-gray-200 tw-rounded-lg tw-px-3 tw-py-2.5">
                <div className="tw-min-w-0">
                  <div className="tw-font-semibold tw-text-gray-800 tw-text-sm">{r.title}</div>
                  <div className="tw-text-xs tw-text-gray-500">{r.desc}{r.dated && date ? <span className="tw-text-gray-400"> — {fmt(date)}</span> : null}</div>
                </div>
                <Btn variant="primary" onClick={() => r.period ? downloadPeriod(r.key) : downloadDated(r.key)}
                  disabled={r.dated && !date || r.period && period === "single" && !date || r.period && period === "custom" && (!from || !to)}>Download</Btn>
              </li>
            )}
          </ul>
        </>}
    </Modal>
  );
}

// ---- Paste feedback dialog ----
// Paste a weekly Orchid feedback email; the backend (OpenAI) parses it into
// draft issues for review. Save groups drafts by policy and reuses CreateIssues
// (which auto-handles non-Four-Site policy numbers), so no new save endpoint.
function PasteFeedbackDialog({ open, onClose, sections, categoriesByArea, onSaved }) {
  const [text, setText] = useState("");
  const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
  const [drafts, setDrafts] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) { setText(""); setDrafts([]); setError(""); setReportDate(new Date().toISOString().slice(0, 10)); }
  }, [open]);

  const parse = async () => {
    if (!text.trim()) { toast.error("Paste the feedback email text first."); return; }
    setParsing(true); setError("");
    try {
      const res = await apiOrchidFeedback.parseFeedback(text.trim(), reportDate);
      if (res.data.status === 200) {
        const ds = (res.data.result?.drafts || []).map((d) => ({ ...d, missingPhotoSectionId: String(d.missingPhotoSectionId || "") }));
        setDrafts(ds);
        if (res.data.result?.reportDate) { setReportDate(res.data.result.reportDate); }
        if (ds.length === 0) { toast.error("No issues were found in that text."); }
      } else {
        setError(res.data.message || res.data.errors && res.data.errors[0]?.message || "Could not parse the feedback.");
      }
    } catch (e) {
      setError("Could not parse the feedback. Try again.");
    } finally {
      setParsing(false);
    }
  };

  const updateRow = (idx, patch) => setDrafts((ds) => ds.map((d, i) => i === idx ? { ...d, ...patch } : d));
  const removeRow = (idx) => setDrafts((ds) => ds.filter((_, i) => i !== idx));

  const save = async () => {
    if (drafts.length === 0) { return; }
    if (!reportDate) { toast.error("A report date is required."); return; }
    setSaving(true);
    // Group drafts by policy number; CreateIssues takes one policy + its issues.
    const groups = {};
    drafts.forEach((d) => {
      const p = (d.policyNo || "").trim();
      if (!p) { return; }
      (groups[p] = groups[p] || []).push(d);
    });
    let failed = 0;
    for (const policyNo of Object.keys(groups)) {
      const issues = groups[policyNo].map((d) => ({
        reportedInReportDate: reportDate,
        concernArea: d.concernArea,
        concernCategory: d.concernCategory,
        missingPhotoSectionId: d.missingPhotoSectionId === "" ? 0 : Number(d.missingPhotoSectionId),
        orchidNotes: d.orchidNotes,
      }));
      try {
        const res = await apiOrchidFeedback.createIssues(policyNo, issues);
        if (res.data.status !== 200) { failed++; }
      } catch (e) { failed++; }
    }
    setSaving(false);
    if (failed === 0) { onSaved(); }
    else { toast.error(`${failed} policy group(s) failed to save; review and retry.`); }
  };

  const activeSections = sections.filter((s) => s.active);

  return (
    <Modal open={open} onClose={onClose} title="Paste Orchid feedback" wide
      footer={<>
        <Btn onClick={onClose}>Cancel</Btn>
        {drafts.length > 0
          ? <Btn variant="primary" onClick={save} disabled={saving}>{saving ? "Saving..." : `Save ${drafts.length} issue${drafts.length === 1 ? "" : "s"}`}</Btn>
          : <Btn variant="primary" onClick={parse} disabled={parsing}>{parsing ? "Parsing..." : "Parse"}</Btn>}
      </>}>
      {error && <div className="tw-mb-3 tw-text-sm tw-text-rose-600">{error}</div>}
      {drafts.length === 0
        ? <>
          <Field label="Reported in report dated" required hint="The date the issues were raised (the feedback email date).">
            <TextInput type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} />
          </Field>
          <Field label="Feedback email" hint="Paste the email body (Photo Concerns / Data Concerns sections). It is parsed into draft issues for you to review before saving.">
            <TextArea rows={14} value={text} onChange={(e) => setText(e.target.value)} autoFocus placeholder={"Photo Concerns\nMissing required photos\ni. MSTO-TX-0001578-00- HVAC & label\n..."} />
          </Field>
        </>
        : <>
          <div className="tw-text-sm tw-text-gray-500 tw-mb-3">Review the parsed issues. Edit any field, remove rows that are not relevant, then save. Saved issues land as untriaged for normal triage.</div>
          <div className="tw-overflow-x-auto">
            <table className="tw-min-w-full tw-divide-y tw-divide-gray-100 tw-text-sm">
              <thead>
                <tr className="tw-bg-sky-50 tw-text-gray-500 tw-text-xs tw-uppercase tw-tracking-wider">
                  <th className="tw-px-2 tw-py-2 tw-text-left">Policy</th>
                  <th className="tw-px-2 tw-py-2 tw-text-left">Area</th>
                  <th className="tw-px-2 tw-py-2 tw-text-left">Category</th>
                  <th className="tw-px-2 tw-py-2 tw-text-left">Photo section</th>
                  <th className="tw-px-2 tw-py-2 tw-text-left">Notes</th><th></th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-gray-50">
                {drafts.map((d, idx) => {
                  const cats = categoriesByArea[d.concernArea] || [];
                  return (
                    <tr key={idx}>
                      <td className="tw-px-2 tw-py-2"><TextInput value={d.policyNo} onChange={(e) => updateRow(idx, { policyNo: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-2">
                        <Select value={d.concernArea} onChange={(e) => updateRow(idx, { concernArea: e.target.value, concernCategory: categoriesByArea[e.target.value]?.[0]?.value || "", missingPhotoSectionId: "" })}>
                          <option value="photo">Photo</option><option value="data">Data</option>
                        </Select>
                      </td>
                      <td className="tw-px-2 tw-py-2">
                        <Select value={d.concernCategory} onChange={(e) => updateRow(idx, { concernCategory: e.target.value })}>
                          {cats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </Select>
                      </td>
                      <td className="tw-px-2 tw-py-2">
                        {d.concernArea === "photo"
                          ? <Select value={d.missingPhotoSectionId} onChange={(e) => updateRow(idx, { missingPhotoSectionId: e.target.value })}>
                            <option value="">— None —</option>
                            {activeSections.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                          </Select>
                          : <span className="tw-text-gray-300">—</span>}
                      </td>
                      <td className="tw-px-2 tw-py-2 tw-min-w-[16rem]"><TextArea rows={2} value={d.orchidNotes} onChange={(e) => updateRow(idx, { orchidNotes: e.target.value })} /></td>
                      <td className="tw-px-2 tw-py-2"><button className="tw-text-rose-500 tw-text-xs hover:tw-underline" onClick={() => removeRow(idx)}>Remove</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>}
    </Modal>
  );
}

// ---- Manage photo sections ----
function PhotoSectionsDialog({ open, onClose }) {
  const [sections, setSections] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const loadSections = () => apiOrchidFeedback.listPhotoSections(true)
    .then((res) => setSections(res.data.result || []))
    .catch(() => {});
  useEffect(() => { if (open) { loadSections(); } }, [open]);

  const add = async () => {
    if (!name.trim()) {return;}
    setSaving(true);
    const res = await apiOrchidFeedback.createPhotoSection(name.trim(), true, sections.length);
    setSaving(false);
    if (res.data.status === 200) { setName(""); loadSections(); toast.success("Section added"); }
    else {toast.error("Failed to add section");}
  };

  const toggleActive = async (s) => {
    const res = await apiOrchidFeedback.updatePhotoSection(s.id, s.name, !s.active, s.position);
    if (res.data.status === 200) {loadSections();}
    else {toast.error("Failed to update");}
  };

  const rename = async (s) => {
    const next = window.prompt("Rename photo section", s.name);
    if (next && next.trim() && next.trim() !== s.name) {
      const res = await apiOrchidFeedback.updatePhotoSection(s.id, next.trim(), s.active, s.position);
      if (res.data.status === 200) {loadSections();}
      else {toast.error("Failed to rename");}
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) {return;}
    const res = await apiOrchidFeedback.deletePhotoSection(s.id);
    if (mutationOk(res)) { loadSections(); toast.success("Deleted"); }
    else { toast.error(res.data && res.data.message || "Failed to delete (may be in use)"); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Manage Photo Sections"
      footer={<Btn onClick={onClose}>Done</Btn>}>
      <div className="tw-flex tw-gap-2 tw-mb-4">
        <TextInput placeholder="New section name (e.g. Water Heater label)" value={name}
          onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <Btn variant="primary" onClick={add} disabled={saving || !name.trim()}>Add</Btn>
      </div>
      <ul className="tw-divide-y tw-divide-gray-100">
        {sections.length === 0 && <li className="tw-text-gray-400 tw-text-sm tw-py-4">No sections yet — add “Water Heater label” and any others you report on.</li>}
        {sections.map((s) => 
          <li key={s.id} className="tw-flex tw-items-center tw-gap-3 tw-py-2.5">
            <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)}
              className="tw-h-4 tw-w-4 tw-rounded tw-border-gray-300 tw-text-sky-600 focus:tw-ring-sky-300" />
            <span className={`tw-flex-1 ${s.active ? "tw-text-gray-800" : "tw-text-gray-400 tw-line-through"}`}>{s.name}</span>
            <button className="tw-text-sky-600 tw-text-sm hover:tw-underline" onClick={() => rename(s)}>Rename</button>
            <button className="tw-text-rose-600 tw-text-sm hover:tw-underline" onClick={() => remove(s)}>Delete</button>
          </li>
        )}
      </ul>
    </Modal>
  );
}